import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type VehicleType = 'suv' | 'truck' | 'sxs' | 'atv' | 'dirt_bike' | 'other';

export interface UserVehicle {
  id: string;
  user_id: string;
  year: number;
  make: string;
  model: string;
  trim: string;
  nickname: string;
  modifications: string;
  vehicle_type: VehicleType | null;
  photo_urls: any;
  primary_photo_url: string;
  is_featured: boolean;
  api_cache: any;
  created_at: string;
  updated_at: string;
}

interface CreateVehicleData {
  year: number;
  make: string;
  model: string;
  trim?: string;
  nickname?: string;
  modifications?: string;
  vehicle_type?: VehicleType | null;
  primary_photo_url?: string;
  photo_urls?: string[];
  is_featured?: boolean;
}

interface UpdateVehicleData extends Partial<CreateVehicleData> {
  id: string;
}

export const useUserVehicles = (userId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['user-vehicles', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID required');

      const { data, error } = await supabase
        .from('user_vehicles')
        .select('*')
        .eq('user_id', userId)
        .order('is_featured', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as UserVehicle[];
    },
    enabled: !!userId,
  });

  const createMutation = useMutation({
    mutationFn: async (vehicleData: CreateVehicleData) => {
      if (!userId) throw new Error('User ID required');

      const { data, error } = await supabase
        .from('user_vehicles')
        .insert([{
          user_id: userId,
          year: vehicleData.year,
          make: vehicleData.make,
          model: vehicleData.model,
          trim: vehicleData.trim || '',
          nickname: vehicleData.nickname || '',
          modifications: vehicleData.modifications || '',
          vehicle_type: vehicleData.vehicle_type || null,
          primary_photo_url: vehicleData.primary_photo_url || '',
          photo_urls: vehicleData.photo_urls || [],
          is_featured: vehicleData.is_featured || false,
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-vehicles', userId] });
      toast({
        title: 'Vehicle added',
        description: 'Your vehicle has been added to your garage.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to add vehicle',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: UpdateVehicleData) => {
      const { data, error } = await supabase
        .from('user_vehicles')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-vehicles', userId] });
      toast({
        title: 'Vehicle updated',
        description: 'Your vehicle has been updated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update vehicle',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (vehicleId: string) => {
      const { error } = await supabase
        .from('user_vehicles')
        .delete()
        .eq('id', vehicleId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-vehicles', userId] });
      toast({
        title: 'Vehicle removed',
        description: 'Your vehicle has been removed from your garage.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to remove vehicle',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const setFeaturedMutation = useMutation({
    mutationFn: async (vehicleId: string) => {
      if (!userId) throw new Error('User ID required');

      // First, unfeatured all vehicles for this user
      const { error: unfeaturedError } = await supabase
        .from('user_vehicles')
        .update({ is_featured: false })
        .eq('user_id', userId);

      if (unfeaturedError) throw unfeaturedError;

      // Then feature the selected vehicle
      const { data, error } = await supabase
        .from('user_vehicles')
        .update({ is_featured: true })
        .eq('id', vehicleId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-vehicles', userId] });
      toast({
        title: 'Featured vehicle updated',
        description: 'Your featured vehicle has been updated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to set featured vehicle',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    vehicles: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    createVehicle: createMutation.mutate,
    createVehicleAsync: createMutation.mutateAsync,
    updateVehicle: updateMutation.mutate,
    deleteVehicle: deleteMutation.mutate,
    setFeaturedVehicle: setFeaturedMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isSettingFeatured: setFeaturedMutation.isPending,
  };
};

export const useFeaturedVehicle = (userId?: string) => {
  return useQuery({
    queryKey: ['featured-vehicle', userId],
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from('user_vehicles')
        .select('*')
        .eq('user_id', userId)
        .eq('is_featured', true)
        .maybeSingle();

      if (error) throw error;
      return data as UserVehicle | null;
    },
    enabled: !!userId,
  });
};
